import {
    ArtistImage,
} from "../../pages/AddArtist/style"
import { ReactComponent as SpotifyFollowersIcon } from "assets/images/streaming/icons/spotify-i.svg"
import millify from "millify"
import { SpotifyArtistInBrandConnections, SpotifyArtistSearchResponse } from "modules/Types"
import NotFoundArtistImage from "assets/images/not-found-user.png"
import ButtonSymphony from "elements/ButtonSymphony"
import { clsx, styx } from 'modules/Utils'
import { CSSProperties } from "react"
import Colors from "modules/Colors"
import useBrand from "Hooks/useBrand"
import Icon from "elements/Icon"
import Sty from "modules/Sty"

/**
 * Gets the image URL for an artist
 * @param artist - The artist object to get the image URL from
 * @returns The image URL if found, undefined otherwise
 */
const getImageUrl = (artist?: SpotifyArtistInBrandConnections | SpotifyArtistSearchResponse | null) => {
    if (!artist) {
        return undefined
    }
    if ((artist as SpotifyArtistInBrandConnections).images) {
        return (artist as SpotifyArtistInBrandConnections).images[0]?.url
    } else if ((artist as SpotifyArtistSearchResponse).thumbnail_url) {
        return (artist as SpotifyArtistSearchResponse).thumbnail_url
    }

    return undefined
}

/**
 * Gets the formatted follower count for an artist
 * @param artist - The artist object to get follower count from
 * @returns Formatted follower count string
 */
const getFollowers = (artist?: SpotifyArtistInBrandConnections | SpotifyArtistSearchResponse | null) => {
    const { followers } = artist || {}

    if ((artist as SpotifyArtistInBrandConnections).followers?.total) {
        return millify((artist as SpotifyArtistInBrandConnections).followers.total)
    }
    if ((artist as SpotifyArtistSearchResponse)?.followers) {
        return millify((artist as SpotifyArtistSearchResponse).followers || 0)
    }

    return 0
}

interface ArtistItemProps {
    className?: string
    style?: CSSProperties
    /** Whether the item is in a loading state */
    isLoadingSelectedArtist?: boolean
    /** Whether this artist is currently selected */
    isSelected?: boolean
    /** The artist data to display */
    artist: SpotifyArtistInBrandConnections | SpotifyArtistSearchResponse
    /** Label text for the connect button */
    buttonLabel: string
    /** Callback function when artist is selected */
    selectArtist: (artist: SpotifyArtistInBrandConnections | SpotifyArtistSearchResponse) => void
}

const ArtistItem = ({
    className,
    style,
    isLoadingSelectedArtist,
    isSelected,
    artist,
    buttonLabel,
    selectArtist,
}: ArtistItemProps) => {
    const { isLoadingBrand } = useBrand();
    const imageUrl = getImageUrl(artist)
    const followers = getFollowers(artist)

    const handleClickItem = () => {
        if (!isLoadingSelectedArtist) {
            selectArtist(artist)
        }
    }

    return (
        <div
            className={clsx('artistItemComponent', className)}
            style={styx(sty.artistItemComponent, style)}
        >
            <div className="artistItemContainer" style={sty.artistItemContainer}>
                <img className="artistImage" src={imageUrl || NotFoundArtistImage} style={sty.artistImage} />
                <div className="artistNameContainer" style={sty.artistNameContainer}>
                    <div className="artistNameLabel" style={sty.artistNameLabel}>{artist?.name}</div>
                    <div className="artistFollowersContainer" style={sty.artistFollowersContainer}>
                        <Icon
                            className="artistFollowersPlatform"
                            icon="spotifyColor"
                            size={20}
                        />
                        <div className="artistFollowers" style={sty.artistFollowers}>
                            {followers} followers
                        </div>
                    </div>
                </div>
            </div>
            <ButtonSymphony
                className="artistItemButtonGoToDashboard"
                onClick={handleClickItem}
                disabled={isLoadingSelectedArtist || isLoadingBrand}
                variant="text"
                isLoading={isSelected && (isLoadingSelectedArtist || isLoadingBrand)}
            >
                {buttonLabel}
            </ButtonSymphony>
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    artistItemComponent: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: 8,
        paddingRight: 16,
        paddingBottom: 8,
        paddingLeft: 8,
        gap: 16,
        width: '100%',
        borderBottom: Sty.border,
        borderRadius: 16,
        backgroundColor: Colors.white
    },
    artistItemContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        overflow: 'hidden'
    },
    artistImage: {
        width: 60,
        height: 60,
        borderRadius: 16,
        objectFit: 'cover'
    },
    artistNameContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 0,
    },
    artistNameLabel: {
        ...Sty.text16
    },
    artistFollowersContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        padding: 0,
        gap: 4,
    },
    artistFollowers: {
        ...Sty.text14grey,
    },
    artistFollowersPlatform: {
        width: 16,
        height: 16
    },
    connectArtistLabel: {
        fontSize: 16,
        display: 'flex',
        alignItems: 'center',
        textAlign: 'right',
        color: '#8800ff',
        cursor: 'pointer'
    }
}

export default ArtistItem
